import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Stack,
  Breadcrumbs,
  Link,
  Avatar,
  TextField,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import AlertItem from '../../Components/AlertItem';
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getDocumentVersionDetail,
  updateDocumentVersion,
  uploadDocumentVersion,
  deleteDocumentVersion,
  downloadDocumentVersion,
  downloadDocumentVersionBlob,
  getFileExtensionFromContentType,
} from '../../endpoints/documents';
import { processLinkUrl } from '../../utils/urlUtils';
import dayjs from 'dayjs';

const DocumentVersionDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id, versionId } = useParams<{ id: string; versionId: string }>();

  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    description: '',
    video_url: '',
    link_url: '',
    is_active: false,
    is_public: false,
    is_draft: false,
    read_only: false,
  });
  const [linkUrlError, setLinkUrlError] = useState<string>('');
  const [videoUrlError, setVideoUrlError] = useState<string>('');
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');

  // Fetch document data
  const {
    data: document,
    isLoading,
    error,
    refetch,
  } = useApiGet(
    ['document-version-detail', id, versionId],
    () => getDocumentVersionDetail(id!, versionId || 'current'),
    { enabled: !!id }
  );

  // Populate edit form data when document loads
  useEffect(() => {
    if (document) {
      setEditFormData({
        description: document.description || '',
        video_url: document.video_url || '',
        link_url: document.link_url || '',
        is_active: document.is_active || false,
        is_public: document.is_public || false,
        is_draft: document.is_draft || false,
        read_only: document.read_only || false,
      });
    }
  }, [document]);

  const handleBackToDocuments = () => {
    navigate('/library');
  };

  // Function to detect document type and determine viewing method
  const getDocumentType = (
    url: string,
    contentType?: string
  ): 'pdf' | 'image' | 'office' | 'text' | 'other' => {
    const urlLower = url.toLowerCase();

    // Check content type first (more reliable)
    if (contentType) {
      const contentTypeLower = contentType.toLowerCase();
      if (contentTypeLower.includes('application/pdf')) return 'pdf';
      if (contentTypeLower.includes('image/')) return 'image';
      if (
        contentTypeLower.includes(
          'application/vnd.openxmlformats-officedocument'
        ) ||
        contentTypeLower.includes('application/msword') ||
        contentTypeLower.includes('application/vnd.ms-')
      )
        return 'office';
      if (contentTypeLower.includes('text/')) return 'text';
    }

    // Fallback to URL extension detection
    if (urlLower.includes('.pdf')) return 'pdf';
    if (
      urlLower.includes('.jpg') ||
      urlLower.includes('.jpeg') ||
      urlLower.includes('.png') ||
      urlLower.includes('.gif') ||
      urlLower.includes('.webp') ||
      urlLower.includes('.svg')
    )
      return 'image';
    if (
      urlLower.includes('.docx') ||
      urlLower.includes('.doc') ||
      urlLower.includes('.pptx') ||
      urlLower.includes('.ppt') ||
      urlLower.includes('.xlsx') ||
      urlLower.includes('.xls')
    )
      return 'office';
    if (
      urlLower.includes('.txt') ||
      urlLower.includes('.html') ||
      urlLower.includes('.css') ||
      urlLower.includes('.js') ||
      urlLower.includes('.json') ||
      urlLower.includes('.xml')
    )
      return 'text';

    return 'other';
  };

  const handleViewDocument = async () => {
    try {
      const response = await downloadDocumentVersion(
        id!,
        versionId || 'current'
      );

      const documentType = getDocumentType(response.url);

      switch (documentType) {
        case 'pdf':
          // Use Google Docs Viewer as default for better compatibility
          const googleDocsUrl = `https://docs.google.com/gview?url=${encodeURIComponent(response.url)}&embedded=true`;
          setPdfUrl(googleDocsUrl);
          setShowPdfViewer(true);
          break;
        case 'image':
          // Open images in embedded viewer (better than new tab)
          setPdfUrl(response.url);
          setShowPdfViewer(true);
          break;
        case 'office':
          // Office documents work best with Google Docs viewer or Microsoft Office Online
          // For now, open in new tab - could be enhanced with embedded viewer later
          window.open(response.url, '_blank');
          break;
        case 'text':
          // Text files can be opened in embedded viewer
          setPdfUrl(response.url);
          setShowPdfViewer(true);
          break;
        default:
          // Other document types open in new tab
          window.open(response.url, '_blank');
          break;
      }
    } catch (error) {
      console.error('Error opening document:', error);
      alert('Failed to open document. Please try again.');
    }
  };

  const handleDownloadDocument = async () => {
    try {
      const response = await downloadDocumentVersionBlob(
        id!,
        versionId || 'current'
      );

      // Determine the filename with proper extension
      let filename = response.filename;
      if (!filename) {
        // Fallback to document name if no filename from API
        const baseName =
          document?.document_name ||
          `document-${id}-version-${versionId || 'current'}`;
        const extension = response.contentType
          ? getFileExtensionFromContentType(response.contentType)
          : '';
        filename = baseName + extension;
      }

      // Create a download link
      const url = window.URL.createObjectURL(response.blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = filename;

      // Trigger download
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document. Please try again.');
    }
  };

  const handleEditVersion = () => {
    setEditMode(!editMode);
  };

  // Upload document mutation with query invalidation
  const { mutate: uploadDocumentMutation, isPending: isUploading } = useApiSend(
    (file: File) => uploadDocumentVersion(id!, versionId || 'current', file),
    (data) => {
      // Show success message or default message
      if (data.message) {
        alert(data.message);
      } else {
        alert('Document uploaded successfully!');
      }
      // Refetch document data to get fresh data
      refetch();
    },
    (error) => {
      console.error('Error uploading document:', error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      alert(`Failed to upload document: ${errorMessage}`);
    },
    [
      ['document-version-detail', id, versionId],
      ['document-edit', id],
      ['documents-edit'],
      ['documents-library'],
      ['documents'],
    ]
  );

  // Delete document mutation with query invalidation
  const { mutate: deleteDocumentMutation, isPending: isDeleting } = useApiSend(
    () => deleteDocumentVersion(id!, versionId || 'current'),
    (data) => {
      // Show success message or default message
      if (data.message) {
        alert(data.message);
      } else {
        alert('Document removed successfully!');
      }
      // Refetch document data to get fresh data
      refetch();
    },
    (error) => {
      console.error('Error removing document:', error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      alert(`Failed to remove document: ${errorMessage}`);
    },
    [
      ['document-version-detail', id, versionId],
      ['document-edit', id],
      ['documents-edit'],
      ['documents-library'],
      ['documents'],
    ]
  );

  const handleUploadDocument = async (file: File) => {
    uploadDocumentMutation(file);
  };

  const handleRemoveDocument = async () => {
    if (
      !window.confirm(
        'Are you sure you want to remove this document file? This action cannot be undone.'
      )
    ) {
      return;
    }

    deleteDocumentMutation(undefined);
  };

  const handleCopyLink = async () => {
    if (id) {
      const link = `${window.location.origin}/library/document/${id}/latest`;
      try {
        await navigator.clipboard.writeText(link);
        console.log('Link copied:', link);
        // TODO: Show success toast or notification
      } catch (err) {
        console.error('Failed to copy link:', err);
        // Fallback: select the text
        const textArea = window.document.createElement('textarea');
        textArea.value = link;
        window.document.body.appendChild(textArea);
        textArea.select();
        window.document.execCommand('copy');
        window.document.body.removeChild(textArea);
      }
    }
  };

  const { mutate: updateVersionMutation, isPending: isUpdating } = useApiSend(
    (data: {
      description: string;
      video_url: string;
      link_url: string;
      is_active: boolean;
      is_public: boolean;
      is_draft: boolean;
      read_only: boolean;
    }) =>
      updateDocumentVersion(id!, versionId!, {
        version: document?.version || '',
        description: data.description,
        video_url: data.video_url,
        link_url: data.link_url,
        is_active: data.is_active,
        is_public: data.is_public,
        is_draft: data.is_draft,
        read_only: data.read_only,
      }),
    (data) => {
      console.log('Version updated successfully:', data);
      setEditMode(false);
      // Refresh the document data
      refetch();
    },
    (error) => {
      console.error('Error updating version:', error);
      // Keep edit mode on so user can fix and retry
    },
    [['document-version-detail', id, versionId]]
  );

  const handleSave = () => {
    // Validate video_url if it's provided
    if (editFormData.video_url.trim()) {
      const videoUrl = editFormData.video_url.trim();

      // Check if it's an internal link (starts with /) or external link (starts with http:// or https://)
      if (
        !videoUrl.startsWith('/') &&
        !videoUrl.startsWith('http://') &&
        !videoUrl.startsWith('https://')
      ) {
        setVideoUrlError(
          'Video URL must start with / for internal links or http:///https:// for external links'
        );
        return;
      }

      // Clear any previous errors
      setVideoUrlError('');
    }

    // Validate link_url if it's provided
    if (editFormData.link_url.trim()) {
      const linkUrl = editFormData.link_url.trim();

      // Check if it's an internal link (starts with /) or external link (starts with http:// or https://)
      if (
        !linkUrl.startsWith('/') &&
        !linkUrl.startsWith('http://') &&
        !linkUrl.startsWith('https://')
      ) {
        setLinkUrlError(
          'Link URL must start with / for internal links or http:///https:// for external links'
        );
        return;
      }

      // Clear any previous errors
      setLinkUrlError('');
    }

    updateVersionMutation(editFormData);
  };

  const handleCancel = () => {
    setEditMode(false);
    // Reset form data to original values
    if (document) {
      setEditFormData({
        description: document.description || '',
        video_url: document.video_url || '',
        link_url: document.link_url || '',
        is_active: document.is_active || false,
        is_public: document.is_public || false,
        is_draft: document.is_draft || false,
        read_only: document.read_only || false,
      });
    }
  };

  const handleDeleteVersion = () => {
    // TODO: Implement delete version functionality
    console.log('Deleting version:', versionId);
  };

  const handleCopyPublicUrl = async () => {
    if (document?.public_url) {
      try {
        await navigator.clipboard.writeText(document.public_url);
        // TODO: Show success toast or notification
        console.log('Public URL copied to clipboard');
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        // Fallback: select the text
        const textArea = window.document.createElement('textarea');
        textArea.value = document.public_url;
        window.document.body.appendChild(textArea);
        textArea.select();
        window.document.execCommand('copy');
        window.document.body.removeChild(textArea);
      }
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading document version...</Typography>
      </Box>
    );
  }

  if (error || !document) {
    return (
      <Box sx={{ p: 3 }}>
        <AlertItem
          severity="error"
          description="Error loading document version"
          onClose={() => {}}
          keepOpen={true}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={handleBackToDocuments}
          sx={{ cursor: 'pointer' }}
        >
          Documents
        </Link>
        <Link
          component={RouterLink}
          to={`/library/${id}/edit`}
          variant="body2"
          sx={{ cursor: 'pointer' }}
        >
          {document?.document_name}
        </Link>
        <Typography variant="body2" color="text.primary">
          Version {document?.version}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {document.document_name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={`Version ${document?.version}`}
              color="primary"
              variant="outlined"
            />
            <Typography variant="body2" color="text.secondary">
              Created on{' '}
              {document?.created_at
                ? dayjs(document.created_at).format('MM/DD/YYYY')
                : dayjs().format('MM/DD/YYYY')}
            </Typography>
          </Box>
        </Box>

        {/* Header Actions */}
        {!editMode && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ViewIcon />}
              onClick={() => navigate(`/library/${id}/view/${versionId}`)}
              sx={{
                color: '#0072CE',
                borderColor: '#0072CE',
                '&:hover': {
                  borderColor: '#005a9e',
                  backgroundColor: 'rgba(0, 114, 206, 0.04)',
                },
              }}
            >
              Preview
            </Button>
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Document Version Information */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Version Details
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Version Number
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {document?.version}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editFormData.description}
                      onChange={(e) =>
                        setEditFormData((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      multiline
                      rows={3}
                      sx={{ mb: 2 }}
                    />
                  ) : (
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {document?.description || 'No description available'}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Created By
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Avatar sx={{ width: 24, height: 24 }}>
                      <PersonIcon />
                    </Avatar>
                    <Typography variant="body1">
                      {document?.created_by || 'admin@peo.org'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Created Date
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <CalendarIcon fontSize="small" />
                    <Typography variant="body1">
                      {document?.created_at
                        ? dayjs(document.created_at).format('MM/DD/YYYY')
                        : dayjs().format('MM/DD/YYYY')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Downloadable
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={document?.downloadable ? 'Yes' : 'No'}
                      size="small"
                      color={document?.downloadable ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Active
                  </Typography>
                  {editMode ? (
                    <Box sx={{ mb: 2 }}>
                      <Switch
                        checked={editFormData.is_active}
                        onChange={(e) => {
                          const newActiveState = e.target.checked;
                          setEditFormData((prev) => ({
                            ...prev,
                            is_active: newActiveState,
                            // If setting to active, ensure draft is false
                            is_draft: newActiveState ? false : prev.is_draft,
                          }));
                        }}
                        disabled={editFormData.is_draft}
                        color="success"
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1, display: 'inline' }}
                      >
                        {editFormData.is_active ? 'Active' : 'Inactive'}
                      </Typography>
                      {editFormData.is_draft && (
                        <Typography
                          variant="caption"
                          color="error"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          Cannot be active while in draft mode
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={document?.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={document?.is_active ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Public
                  </Typography>
                  {editMode ? (
                    <Box sx={{ mb: 2 }}>
                      <Switch
                        checked={editFormData.is_public}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            is_public: e.target.checked,
                          }))
                        }
                        color="primary"
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1, display: 'inline' }}
                      >
                        {editFormData.is_public ? 'Public' : 'Private'}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={document?.is_public ? 'Public' : 'Private'}
                        size="small"
                        color={document?.is_public ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Draft
                  </Typography>
                  {editMode ? (
                    <Box sx={{ mb: 2 }}>
                      <Switch
                        checked={editFormData.is_draft}
                        onChange={(e) => {
                          const newDraftState = e.target.checked;
                          setEditFormData((prev) => ({
                            ...prev,
                            is_draft: newDraftState,
                            // If setting to draft, ensure active is false
                            is_active: newDraftState ? false : prev.is_active,
                          }));
                        }}
                        disabled={editFormData.is_active}
                        color="warning"
                      />
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, display: 'inline' }}
                      >
                        {editFormData.is_draft ? 'Draft' : 'Final'}
                      </Typography>
                      {editFormData.is_active && (
                        <Typography
                          variant="caption"
                          color="error"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          Cannot be draft while active
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={document?.is_draft ? 'Draft' : 'Final'}
                        size="small"
                        color={document?.is_draft ? 'warning' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Read Only
                  </Typography>
                  {editMode ? (
                    <Box sx={{ mb: 2 }}>
                      <Switch
                        checked={editFormData.read_only}
                        onChange={(e) =>
                          setEditFormData((prev) => ({
                            ...prev,
                            read_only: e.target.checked,
                          }))
                        }
                        color="info"
                      />
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, display: 'inline' }}
                      >
                        {editFormData.read_only ? 'Read Only' : 'Editable'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        {editFormData.read_only
                          ? 'Document will be view-only with restricted access'
                          : 'Document can be downloaded and edited normally'}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={document?.read_only ? 'Read Only' : 'Editable'}
                        size="small"
                        color={document?.read_only ? 'info' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Video URL
                  </Typography>
                  {editMode ? (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        value={editFormData.video_url}
                        onChange={(e) => {
                          setEditFormData((prev) => ({
                            ...prev,
                            video_url: e.target.value,
                          }));
                          // Clear error when user starts typing
                          if (videoUrlError) {
                            setVideoUrlError('');
                          }
                        }}
                        placeholder="Enter video URL"
                        error={!!videoUrlError}
                        sx={{ mb: 2 }}
                      />
                      {videoUrlError && (
                        <Typography
                          variant="caption"
                          color="error"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          {videoUrlError}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {document?.video_url ? (
                        <Link
                          href={document.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {document.video_url}
                        </Link>
                      ) : (
                        'No video URL set'
                      )}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Link URL
                  </Typography>
                  {editMode ? (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        value={editFormData.link_url}
                        onChange={(e) => {
                          setEditFormData((prev) => ({
                            ...prev,
                            link_url: e.target.value,
                          }));
                          // Clear error when user starts typing
                          if (linkUrlError) {
                            setLinkUrlError('');
                          }
                        }}
                        placeholder="Enter link URL"
                        error={!!linkUrlError}
                        sx={{ mb: 2 }}
                      />
                      {linkUrlError && (
                        <Typography
                          variant="caption"
                          color="error"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          {linkUrlError}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {document?.link_url ? (
                        <Link
                          href={processLinkUrl(document.link_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {document.link_url}
                        </Link>
                      ) : (
                        'No link URL set'
                      )}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Public URL
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography variant="body1" sx={{ flexGrow: 1 }}>
                      {document?.public_url ? (
                        <Link
                          href={document.public_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {document.public_url}
                        </Link>
                      ) : (
                        'No public URL available'
                      )}
                    </Typography>
                    {document?.public_url && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyPublicUrl}
                        sx={{
                          minWidth: 'auto',
                          px: 1,
                          color: '#0072CE',
                          borderColor: '#0072CE',
                          '&:hover': {
                            borderColor: '#005a9e',
                            backgroundColor: 'rgba(0, 114, 206, 0.04)',
                          },
                        }}
                      >
                        Copy
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          {/* Quick Actions */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Stack spacing={2}>
                {document?.downloadable && (
                  <>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<ViewIcon />}
                      onClick={handleViewDocument}
                      sx={{
                        color: '#FF6B35',
                        borderColor: '#FF6B35',
                        '&:hover': {
                          borderColor: '#E55A2B',
                          backgroundColor: 'rgba(255, 107, 53, 0.04)',
                        },
                      }}
                    >
                      View Document
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadDocument}
                      sx={{
                        backgroundColor: '#00A859',
                        '&:hover': {
                          backgroundColor: '#008A4A',
                        },
                      }}
                    >
                      Download Document
                    </Button>
                  </>
                )}

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyLink}
                  sx={{
                    color: '#6C63FF',
                    borderColor: '#6C63FF',
                    '&:hover': {
                      borderColor: '#5A52E0',
                      backgroundColor: 'rgba(108, 99, 255, 0.04)',
                    },
                  }}
                >
                  Copy Link
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Version Management */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Version Management
              </Typography>
              <Stack spacing={2}>
                {editMode ? (
                  <>
                    <Button
                      fullWidth
                      variant="contained"
                      sx={{
                        backgroundColor: '#0072CE',
                        '&:hover': { backgroundColor: '#005a9e' },
                      }}
                      startIcon={<EditIcon />}
                      onClick={handleSave}
                      disabled={isUpdating}
                    >
                      {isUpdating ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={isUpdating}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{
                      color: '#0072CE',
                      borderColor: '#0072CE',
                      '&:hover': {
                        borderColor: '#005a9e',
                        backgroundColor: 'rgba(0, 114, 206, 0.04)',
                      },
                    }}
                    startIcon={<EditIcon />}
                    onClick={handleEditVersion}
                    disabled={isUpdating}
                  >
                    Edit Version
                  </Button>
                )}
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteVersion}
                >
                  Delete Version
                </Button>

                {/* Document File Management */}
                {!document?.downloadable ? (
                  <Button
                    component="label"
                    fullWidth
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    disabled={isUploading || isDeleting}
                    sx={{
                      color: '#00A859',
                      borderColor: '#00A859',
                      '&:hover': {
                        borderColor: '#008A4A',
                        backgroundColor: 'rgba(0, 168, 89, 0.04)',
                      },
                    }}
                  >
                    <input
                      type="file"
                      hidden
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleUploadDocument(file);
                        }
                      }}
                    />
                    {isUploading ? 'Uploading...' : 'Upload Document'}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleRemoveDocument}
                    disabled={isUploading || isDeleting}
                  >
                    {isDeleting ? 'Removing...' : 'Remove Document'}
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* PDF Viewer Dialog */}
      <Dialog
        open={showPdfViewer}
        onClose={() => setShowPdfViewer(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Document Viewer
          <IconButton onClick={() => setShowPdfViewer(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {pdfUrl && (
            <Box
              sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open(pdfUrl, '_blank')}
                  sx={{ mr: 2 }}
                >
                  Direct PDF View
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={() =>
                    window.open(
                      `https://docs.google.com/gview?url=${encodeURIComponent(pdfUrl)}&embedded=true`,
                      '_blank'
                    )
                  }
                  sx={{ mr: 2 }}
                >
                  Google Docs Viewer
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadDocument}
                >
                  Download
                </Button>
              </Box>
              <Box sx={{ flex: 1, position: 'relative' }}>
                <iframe
                  src={pdfUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title="Document Viewer"
                  onError={() => {
                    console.error('Iframe failed to load PDF');
                    // Could show an error message here
                  }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DocumentVersionDetail;
